import { lazy, Suspense, type ReactNode } from 'react';
import { Navigate, useLocation, useParams, type RouteObject } from 'react-router-dom';
import { RoamLoading } from '@/components/branding/RoamLoading';
import { isLaunchSplashVisible } from '@/lib/pwa/app-launch';
import { tripLaunchLoadingLabel } from '@/lib/trip/tripLaunchLoading';
import { TripAccessBoundary } from '@/components/roam-app/AccessBoundary';
import { ActiveTripRedirectClient } from '@/components/roam-app/ActiveTripRedirectClient';
import type { TripWorkspacePageKind } from '@/features/trip/TripWorkspaceShell';

import { DevOnlyRoute } from './DevOnlyRoute';
import { LandingPageRoute } from './LandingPageRoute';
import { LoginPage } from './LoginPage';
import { NotFoundPage } from './NotFoundPage';
import { RequireAuth } from './RequireAuth';
import { RootLayout } from './RootLayout';
import { RouteErrorBoundary } from './RouteErrorBoundary';
import { SignupPage } from './SignupPage';
import { RequireGuideAuth } from './guides/RequireGuideAuth';
import { HowWeMakeMoneyPage } from './legal/HowWeMakeMoneyPage';
import { LegalLayout } from './legal/LegalLayout';

/**
 * Development-only surfaces are lazy so they never enter the production bundle.
 * `DevOnlyRoute` decides whether to render them; `lazy` decides whether they
 * ship at all.
 */
const EmailPreviewPage = lazy(() => import('./dev/EmailPreviewPage'));
const ItineraryLabPage = lazy(() => import('./dev/ItineraryLabPage'));
const ItineraryWorkspacePage = lazy(() => import('./dev/ItineraryWorkspacePage'));
const GenerativeTripPage = lazy(() => import('./preview/GenerativeTripPage'));
const ChatTilesPhase1Page = lazy(() => import('./preview/ChatTilesPhase1Page'));
const MapViewPage = lazy(() => import('./preview/MapViewPage'));
const TripLayoutChromePage = lazy(() =>
  import('./preview/TripLayoutChromePage').then((module) => ({
    default: module.TripLayoutChromePage,
  })),
);
const TripListCardsPreviewPage = lazy(() => import('./preview/TripListCardsPreviewPage'));
const LoginChromePreviewPage = lazy(() => import('./preview/LoginChromePreviewPage'));
const SettingsFamilyHealthHintPreviewPage = lazy(
  () => import('./preview/SettingsFamilyHealthHintPreviewPage'),
);
const SettingsAccountPasswordPreviewPage = lazy(
  () => import('./preview/SettingsAccountPasswordPreviewPage'),
);
const SettingsHubPreviewPage = lazy(() => import('./preview/SettingsHubPreviewPage'));
const SettingsSubpagePreviewPage = lazy(() => import('./preview/SettingsSubpagePreviewPage'));
const YearViewPreviewPage = lazy(() => import('./preview/YearViewPreviewPage'));
const NewTripPhoneChromePreviewPage = lazy(() => import('./preview/NewTripPhoneChromePreviewPage'));

/**
 * Every real app page is lazy, the same way the dev/preview surfaces above
 * are, so an anonymous landing-page visitor never downloads them before
 * first paint (WS-I item 1, docs/APP-OVERHAUL-PLAN.md). Only the landing
 * page, auth, legal, and 404 routes stay eager. Each renders behind the one
 * shared `Lazy`/`RouteFallback` skeleton defined below.
 */
// The authenticated shell is never needed to paint the anonymous landing page.
// Keep one stable lazy type for every route that uses it.
const RoamAppShell = lazy(() =>
  import('@/components/roam-app/RoamAppShell').then((m) => ({ default: m.RoamAppShell })),
);

const ConfirmationInboxPage = lazy(() => import('./ConfirmationInboxPage'));
const BookingSandboxPage = lazy(() => import('./BookingSandboxPage'));
const NewTripPage = lazy(() => import('./NewTripPage').then((m) => ({ default: m.NewTripPage })));
const FreshTripWizardClient = lazy(() =>
  import('@/components/roam-app/FreshTripWizardClient').then((m) => ({
    default: m.FreshTripWizardClient,
  })),
);
const OnboardingClient = lazy(() =>
  import('@/components/roam-app/OnboardingClient').then((m) => ({ default: m.OnboardingClient })),
);
const ExploreClient = lazy(() =>
  import('@/components/roam-app/ExploreClient').then((m) => ({ default: m.ExploreClient })),
);
const PublicSnapshotDetail = lazy(() =>
  import('@/components/roam-app/PublicSnapshotDetail').then((m) => ({
    default: m.PublicSnapshotDetail,
  })),
);
const TripsListClient = lazy(() =>
  import('@/components/roam-app/TripsListClient').then((m) => ({ default: m.TripsListClient })),
);
const YearViewPage = lazy(() =>
  import('./YearViewPage').then((m) => ({ default: m.YearViewPage })),
);
const TripJoinClient = lazy(() =>
  import('@/components/roam-app/TripJoinClient').then((m) => ({ default: m.TripJoinClient })),
);
const TripEmailInviteJoinClient = lazy(() =>
  import('@/components/roam-app/TripEmailInviteJoinClient').then((m) => ({
    default: m.TripEmailInviteJoinClient,
  })),
);
/**
 * ONE lazy component for all four trip tabs (itinerary/chat/dashboard/sleep),
 * with the page kind carried as a prop instead of as four distinct lazy()
 * objects. React reconciles by element `type`: four separate `lazy()` calls
 * — even against the same module — produce four different types, so
 * switching tabs unmounted and remounted the entire (6,049-line)
 * TripWorkspaceClient tree instead of updating it in place (CI review
 * blocker 1). A single stable module-level reference, varied only by the
 * `page` prop, is what keeps `/trips/:id` → `/trips/:id/chat` an update.
 */
const TripWorkspacePage = lazy(() =>
  import('@/features/trip/TripWorkspaceShell').then((m) => ({
    default: m.TripWorkspaceClient,
  })),
);
const SettingsClient = lazy(() =>
  import('@/components/roam-app/SettingsClient').then((m) => ({ default: m.SettingsClient })),
);
const SettingsHubClient = lazy(() =>
  import('@/components/roam-app/SettingsHubClient').then((m) => ({
    default: m.SettingsHubClient,
  })),
);
const SettingsPrivacyClient = lazy(() =>
  import('@/components/roam-app/SettingsPrivacyClient').then((m) => ({
    default: m.SettingsPrivacyClient,
  })),
);
const SettingsBillingClient = lazy(() =>
  import('@/components/roam-app/SettingsBillingClient').then((m) => ({
    default: m.SettingsBillingClient,
  })),
);
const AccountSettingsPage = lazy(() =>
  import('./AccountSettingsPage').then((m) => ({ default: m.AccountSettingsPage })),
);
const SettingsAccountLegalClient = lazy(() =>
  import('@/components/roam-app/SettingsAccountLegalClient').then((m) => ({
    default: m.SettingsAccountLegalClient,
  })),
);
const MediaAdminClient = lazy(() =>
  import('@/components/roam-app/MediaAdminClient').then((m) => ({ default: m.MediaAdminClient })),
);
const SpendAdminClient = lazy(() =>
  import('@/components/roam-app/SpendAdminClient').then((m) => ({ default: m.SpendAdminClient })),
);
const HelpCenterPage = lazy(() =>
  import('./help/HelpCenterPage').then((m) => ({ default: m.HelpCenterPage })),
);
const TermsPage = lazy(() => import('./legal/TermsPage').then((m) => ({ default: m.TermsPage })));
const PrivacyPage = lazy(() =>
  import('./legal/PrivacyPage').then((m) => ({ default: m.PrivacyPage })),
);
const HealthNoticePage = lazy(() =>
  import('./legal/HealthNoticePage').then((m) => ({ default: m.HealthNoticePage })),
);
const HelpArticlePage = lazy(() =>
  import('./help/HelpArticlePage').then((m) => ({ default: m.HelpArticlePage })),
);
const GuidesHubPage = lazy(() =>
  import('./guides/GuidesHubPage').then((m) => ({ default: m.GuidesHubPage })),
);
const FlyingWithABabyPage = lazy(() =>
  import('./guides/FlyingWithABabyPage').then((m) => ({ default: m.FlyingWithABabyPage })),
);
const DisneyPackingChecklistPage = lazy(() =>
  import('./guides/DisneyPackingChecklistPage').then((m) => ({
    default: m.DisneyPackingChecklistPage,
  })),
);
const BeachWithABabyPage = lazy(() =>
  import('./guides/BeachWithABabyPage').then((m) => ({ default: m.BeachWithABabyPage })),
);
const BeachWithAToddlerPage = lazy(() =>
  import('./guides/BeachWithAToddlerPage').then((m) => ({ default: m.BeachWithAToddlerPage })),
);

/**
 * Full-page spinner matching the pattern already used for `<AuthLoading>`
 * states across the app (e.g. `TripsListClient`, `MediaAdminClient`) — so a
 * lazy chunk's download never reads as a blank screen (AGENTS.md "no blank
 * loads").
 */
function RouteFallback() {
  const { pathname } = useLocation();
  if (isLaunchSplashVisible()) return null;
  return <RoamLoading label={tripLaunchLoadingLabel(pathname)} />;
}

/** Every lazily-loaded real page renders behind this one shared skeleton. */
function Lazy({ children }: { children: ReactNode }) {
  return <Suspense fallback={<RouteFallback />}>{children}</Suspense>;
}

/** Every authed surface renders inside the shell, exactly as the pages did. */
function Shell({ children }: { children: React.ReactNode }) {
  return (
    <Lazy>
      <RoamAppShell>{children}</RoamAppShell>
    </Lazy>
  );
}

/**
 * The shell plus the sign-in bounce the deleted middleware used to perform.
 * Used for exactly the paths it protected: `/trips*` and `/settings*`.
 */
function ProtectedShell({ children }: { children: React.ReactNode }) {
  return (
    <RequireAuth>
      <Lazy>
        <RoamAppShell>{children}</RoamAppShell>
      </Lazy>
    </RequireAuth>
  );
}

/** `/auth` chose its destination from `?mode`, so keep that. */
function AuthEntryRoute() {
  const location = useLocation();
  return <Navigate to={`/login${location.search}`} replace />;
}

/** `redirect(`/x/${param}`)` from the deleted Next stubs, as a route element. */
function RedirectWithParam({ to }: { to: (params: Record<string, string>) => string }) {
  const params = useParams() as Record<string, string>;
  return <Navigate to={to(params)} replace />;
}

/**
 * `page` is data, not a component selector: every one of the four routes
 * below renders through this exact function and, inside it, through the one
 * `TripWorkspacePage` lazy reference above — never a per-page component —
 * so React always sees the same element type at this tree position no
 * matter which trip tab is active. See `routeConfig.test.ts` for the
 * reconciliation-stability check this restores (CI review blocker 1).
 */
export function TripWorkspaceRoute({ page }: { page: TripWorkspacePageKind }) {
  const { id = '' } = useParams();
  return (
    <ProtectedShell>
      {/* Opening a trip you were not invited to, or a bookmark to a deleted
          one, used to render the generic crash screen in a reload loop. */}
      <TripAccessBoundary key={id}>
        <Lazy>
          <TripWorkspacePage tripId={id} page={page} />
        </Lazy>
      </TripAccessBoundary>
    </ProtectedShell>
  );
}

/**
 * `/new-trip`, `?explore=<slug>`, `?draft=<tripWorkspaceId>`. `NewTripPage`
 * reads the params and keys the client on user + navigation so every tap on
 * "Add new trip" opens the chooser blank (see `lib/nav/planner-session.ts`).
 */
function NewTripRoute() {
  return (
    <Shell>
      <Lazy>
        <NewTripPage />
      </Lazy>
    </Shell>
  );
}

function PublicSnapshotRoute() {
  const { slug = '' } = useParams();
  return (
    <Shell>
      <Lazy>
        <PublicSnapshotDetail slug={slug} />
      </Lazy>
    </Shell>
  );
}

/**
 * The route tree, split out from `index.tsx` so it can be imported for
 * testing (`routeConfig.test.ts`) without constructing a browser router —
 * `createBrowserRouter` reaches for `document`/`window` at call time, which
 * don't exist under the plain Node test environment. `index.tsx` is the only
 * file that wraps this in `createBrowserRouter`.
 */
export const routeConfig: RouteObject[] = [
  {
    element: <RootLayout />,
    errorElement: <RouteErrorBoundary />,
    children: [
      { path: '/', element: <LandingPageRoute /> },

      // --- auth ---
      { path: '/login/*', element: <LoginPage /> },
      { path: '/signup/*', element: <SignupPage /> },
      { path: '/sign-up/*', element: <SignupPage /> },
      { path: '/auth', element: <AuthEntryRoute /> },
      { path: '/forgot-password', element: <Navigate to="/login" replace /> },
      { path: '/reset-password', element: <Navigate to="/login" replace /> },
      { path: '/verify', element: <Navigate to="/settings" replace /> },
      { path: '/verify-email', element: <Navigate to="/settings" replace /> },

      {
        path: '/booking-sandbox',
        element: (
          <RequireAuth>
            <Lazy>
              <BookingSandboxPage />
            </Lazy>
          </RequireAuth>
        ),
      },

      // --- trips ---
      {
        path: '/trips',
        element: (
          <ProtectedShell>
            <Lazy>
              <TripsListClient />
            </Lazy>
          </ProtectedShell>
        ),
      },
      {
        path: '/trips/active',
        element: (
          <ProtectedShell>
            <ActiveTripRedirectClient />
          </ProtectedShell>
        ),
      },
      {
        path: '/year',
        element: (
          <ProtectedShell>
            <Lazy>
              <YearViewPage />
            </Lazy>
          </ProtectedShell>
        ),
      },
      { path: '/trips/:id', element: <TripWorkspaceRoute page="itinerary" /> },
      { path: '/trips/:id/chat', element: <TripWorkspaceRoute page="ask-roam" /> },
      {
        path: '/trips/:id/details',
        element: <TripWorkspaceRoute page="dashboard" />,
      },
      { path: '/trips/:id/dashboard', element: <TripWorkspaceRoute page="dashboard" /> },
      { path: '/trips/:id/sleep', element: <TripWorkspaceRoute page="sleep" /> },
      {
        path: '/trips/:id/building',
        element: <RedirectWithParam to={(p) => `/trips/${p['id']}`} />,
      },
      {
        path: '/join/trip/:token',
        element: (
          <Shell>
            <Lazy>
              <TripJoinClient />
            </Lazy>
          </Shell>
        ),
      },
      {
        path: '/join/email-invite/:tripInviteId',
        element: (
          <Shell>
            <Lazy>
              <TripEmailInviteJoinClient />
            </Lazy>
          </Shell>
        ),
      },

      // --- planning ---
      { path: '/new-trip', element: <NewTripRoute /> },
      {
        path: '/new-trip/fresh',
        element: (
          <Shell>
            <Lazy>
              <FreshTripWizardClient />
            </Lazy>
          </Shell>
        ),
      },
      {
        path: '/onboarding',
        element: (
          <Shell>
            <Lazy>
              <OnboardingClient />
            </Lazy>
          </Shell>
        ),
      },

      // --- explore ---
      {
        path: '/explore',
        element: (
          <Shell>
            <Lazy>
              <ExploreClient />
            </Lazy>
          </Shell>
        ),
      },
      { path: '/explore/trips/:slug', element: <PublicSnapshotRoute /> },
      {
        path: '/explore/:slug',
        element: <RedirectWithParam to={(p) => `/explore/trips/${p['slug']}`} />,
      },
      {
        path: '/share/:id',
        element: <RedirectWithParam to={(p) => `/explore/trips/${p['id']}`} />,
      },

      // --- settings ---
      {
        path: '/settings/confirmations',
        element: (
          <ProtectedShell>
            <Lazy>
              <ConfirmationInboxPage />
            </Lazy>
          </ProtectedShell>
        ),
      },
      {
        path: '/settings',
        element: (
          <ProtectedShell>
            <Lazy>
              <SettingsHubClient />
            </Lazy>
          </ProtectedShell>
        ),
      },
      {
        path: '/settings/account/*',
        element: (
          <ProtectedShell>
            <Lazy>
              <AccountSettingsPage />
            </Lazy>
          </ProtectedShell>
        ),
      },
      {
        path: '/settings/account-legal',
        element: (
          <ProtectedShell>
            <Lazy>
              <SettingsAccountLegalClient />
            </Lazy>
          </ProtectedShell>
        ),
      },
      {
        path: '/settings/billing',
        element: (
          <ProtectedShell>
            <Lazy>
              <SettingsBillingClient
                view="all"
                title="Billing"
                subtitle="Your plan, Ask Roam wallet, and prepaid credit."
              />
            </Lazy>
          </ProtectedShell>
        ),
      },
      { path: '/settings/family', element: <Navigate to="/settings/workspace" replace /> },
      {
        path: '/settings/workspace',
        element: (
          <ProtectedShell>
            <Lazy>
              <SettingsClient
                hideBilling
                backHref="/settings"
                pageTitle="Workspace & home"
                pageSubtitle="Your family workspace name and home base for planning."
                section="workspace"
              />
            </Lazy>
          </ProtectedShell>
        ),
      },
      {
        path: '/settings/preferences',
        element: (
          <ProtectedShell>
            <Lazy>
              <SettingsClient
                hideBilling
                backHref="/settings"
                pageTitle="Trip preferences"
                pageSubtitle="Defaults Roam uses when building and editing trips."
                section="preferences"
              />
            </Lazy>
          </ProtectedShell>
        ),
      },
      {
        path: '/settings/ask-roam',
        element: (
          <ProtectedShell>
            <Lazy>
              <SettingsClient
                hideBilling
                backHref="/settings"
                pageTitle="Ask Roam & safety"
                pageSubtitle="How Ask Roam may edit trips, plus emergency contacts and saved preferences."
                section="collaboration"
              />
            </Lazy>
          </ProtectedShell>
        ),
      },
      {
        path: '/settings/travelers',
        element: (
          <ProtectedShell>
            <Lazy>
              <SettingsClient
                hideBilling
                backHref="/settings"
                pageTitle="Travelers"
                pageSubtitle="Ages, needs, and notes for everyone who travels with you."
                section="travelers"
              />
            </Lazy>
          </ProtectedShell>
        ),
      },
      {
        path: '/settings/privacy',
        element: (
          <ProtectedShell>
            <Lazy>
              <SettingsPrivacyClient />
            </Lazy>
          </ProtectedShell>
        ),
      },
      { path: '/upgrade', element: <Navigate to="/settings/billing" replace /> },

      // --- admin ---
      {
        path: '/admin',
        element: (
          <Shell>
            <Lazy>
              <MediaAdminClient />
            </Lazy>
          </Shell>
        ),
      },
      // Both aliases rendered the same admin client under Next; keep them
      // as real routes rather than redirects so bookmarks land unchanged.
      {
        path: '/admin/media',
        element: (
          <Shell>
            <Lazy>
              <MediaAdminClient />
            </Lazy>
          </Shell>
        ),
      },
      {
        path: '/admin/spend',
        element: (
          <Shell>
            <Lazy>
              <SpendAdminClient />
            </Lazy>
          </Shell>
        ),
      },
      {
        path: '/admin/photos',
        element: (
          <Shell>
            <Lazy>
              <MediaAdminClient />
            </Lazy>
          </Shell>
        ),
      },

      // --- legal (canonical /terms and /privacy; /legal/* kept for older links) ---
      {
        path: '/terms',
        element: <LegalLayout />,
        children: [
          {
            index: true,
            element: (
              <Lazy>
                <TermsPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        path: '/privacy',
        element: <LegalLayout />,
        children: [
          {
            index: true,
            element: (
              <Lazy>
                <PrivacyPage />
              </Lazy>
            ),
          },
          {
            path: 'health-notice',
            element: (
              <Lazy>
                <HealthNoticePage />
              </Lazy>
            ),
          },
        ],
      },
      { path: '/legal/terms', element: <Navigate to="/terms" replace /> },
      { path: '/legal/privacy', element: <Navigate to="/privacy" replace /> },
      {
        path: '/legal/privacy/health-notice',
        element: <Navigate to="/privacy/health-notice" replace />,
      },
      {
        path: '/legal',
        element: <LegalLayout />,
        children: [{ path: 'how-we-make-money', element: <HowWeMakeMoneyPage /> }],
      },

      // --- help center (public; signed-in readers get the app shell + menu) ---
      {
        path: '/help',
        element: (
          <Lazy>
            <Shell>
              <HelpCenterPage />
            </Shell>
          </Lazy>
        ),
      },
      {
        path: '/help/:slug',
        element: (
          <Lazy>
            <Shell>
              <HelpArticlePage />
            </Shell>
          </Lazy>
        ),
      },

      // --- Kiersten Brooke gift guides (account required) ---
      {
        path: '/guides',
        element: (
          <RequireGuideAuth>
            <Lazy>
              <GuidesHubPage />
            </Lazy>
          </RequireGuideAuth>
        ),
      },
      {
        path: '/guides/flying-with-a-baby',
        element: (
          <RequireGuideAuth>
            <Lazy>
              <FlyingWithABabyPage />
            </Lazy>
          </RequireGuideAuth>
        ),
      },
      {
        path: '/guides/disney-packing-checklist',
        element: (
          <RequireGuideAuth>
            <Lazy>
              <DisneyPackingChecklistPage />
            </Lazy>
          </RequireGuideAuth>
        ),
      },
      {
        path: '/guides/beach-with-a-baby',
        element: (
          <RequireGuideAuth>
            <Lazy>
              <BeachWithABabyPage />
            </Lazy>
          </RequireGuideAuth>
        ),
      },
      {
        path: '/guides/beach-with-a-toddler',
        element: (
          <RequireGuideAuth>
            <Lazy>
              <BeachWithAToddlerPage />
            </Lazy>
          </RequireGuideAuth>
        ),
      },

      // --- development-only surfaces (dropped from production builds) ---
      {
        path: '/dev/email-preview',
        element: (
          <DevOnlyRoute>
            <EmailPreviewPage />
          </DevOnlyRoute>
        ),
      },
      {
        path: '/dev/itinerary-lab',
        element: (
          <DevOnlyRoute>
            <ItineraryLabPage />
          </DevOnlyRoute>
        ),
      },
      {
        path: '/dev/itinerary-workspace',
        element: (
          <DevOnlyRoute>
            <ItineraryWorkspacePage />
          </DevOnlyRoute>
        ),
      },
      {
        path: '/preview/generative-trip',
        element: (
          <DevOnlyRoute>
            <GenerativeTripPage />
          </DevOnlyRoute>
        ),
      },
      {
        path: '/preview/chat-tiles-phase1',
        element: (
          <DevOnlyRoute>
            <ChatTilesPhase1Page />
          </DevOnlyRoute>
        ),
      },
      {
        path: '/preview/map-view',
        element: (
          <DevOnlyRoute>
            <MapViewPage />
          </DevOnlyRoute>
        ),
      },
      {
        path: '/preview/trip-layout-chrome',
        element: (
          <DevOnlyRoute>
            <TripLayoutChromePage />
          </DevOnlyRoute>
        ),
      },
      {
        path: '/preview/new-trip-phone',
        element: (
          <DevOnlyRoute>
            <Lazy>
              <NewTripPhoneChromePreviewPage />
            </Lazy>
          </DevOnlyRoute>
        ),
      },
      {
        path: '/preview/year-view',
        element: (
          <DevOnlyRoute>
            <Lazy>
              <YearViewPreviewPage />
            </Lazy>
          </DevOnlyRoute>
        ),
      },
      {
        path: '/preview/trip-list-cards',
        element: (
          <DevOnlyRoute>
            <Lazy>
              <TripListCardsPreviewPage />
            </Lazy>
          </DevOnlyRoute>
        ),
      },
      {
        path: '/preview/login-chrome',
        element: (
          <DevOnlyRoute>
            <Lazy>
              <LoginChromePreviewPage />
            </Lazy>
          </DevOnlyRoute>
        ),
      },
      {
        path: '/preview/settings-family-health-hint',
        element: (
          <DevOnlyRoute>
            <Lazy>
              <SettingsFamilyHealthHintPreviewPage />
            </Lazy>
          </DevOnlyRoute>
        ),
      },
      {
        path: '/preview/settings-account-password',
        element: (
          <DevOnlyRoute>
            <Lazy>
              <SettingsAccountPasswordPreviewPage />
            </Lazy>
          </DevOnlyRoute>
        ),
      },
      {
        path: '/preview/settings-hub',
        element: (
          <DevOnlyRoute>
            <Lazy>
              <SettingsHubPreviewPage />
            </Lazy>
          </DevOnlyRoute>
        ),
      },
      {
        path: '/preview/settings-subpage',
        element: (
          <DevOnlyRoute>
            <Lazy>
              <SettingsSubpagePreviewPage />
            </Lazy>
          </DevOnlyRoute>
        ),
      },

      // --- retired /v2 prototype tree ---
      { path: '/v2', element: <Navigate to="/explore" replace /> },
      { path: '/v2/explore', element: <Navigate to="/explore" replace /> },
      {
        path: '/v2/explore/:slug',
        element: <RedirectWithParam to={(p) => `/explore/trips/${p['slug']}`} />,
      },
      { path: '/v2/new-trip', element: <Navigate to="/new-trip" replace /> },
      { path: '/v2/settings', element: <Navigate to="/settings" replace /> },
      { path: '/v2/sign-in/*', element: <Navigate to="/login" replace /> },
      { path: '/v2/sign-up/*', element: <Navigate to="/login" replace /> },
      { path: '/v2/trips', element: <Navigate to="/trips" replace /> },
      {
        path: '/v2/trips/:id',
        element: <RedirectWithParam to={(p) => `/trips/${p['id']}`} />,
      },

      { path: '*', element: <NotFoundPage /> },
    ],
  },
];
